import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Package, ArrowRight } from 'lucide-react';

export const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { t, API, clearCart, getLocalizedText, currency } = useApp();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const currencySymbol = currency === 'eur' ? '€' : 'RON';

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await fetch(`${API}/orders/${orderId}`);
        if (res.ok) {
          const data = await res.json();
          setOrder(data);
        }
      } catch (err) {
        console.error('Failed to fetch order', err);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
      clearCart();
    }
  }, [orderId, API, clearCart]);

  // Poll payment status if coming from Stripe
  useEffect(() => {
    if (!sessionId || paymentStatus === 'paid' || pollCount >= 5) return;

    const pollPayment = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          setPaymentStatus(data.payment_status);
          if (data.payment_status !== 'paid') {
            setPollCount((prev) => prev + 1);
          }
        }
      } catch (err) {
        console.error('Failed to check payment status', err);
      }
    };

    const timer = setTimeout(pollPayment, 2000);
    return () => clearTimeout(timer);
  }, [sessionId, API, paymentStatus, pollCount]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading order details...</p>
        </div>
      </Layout>
    );
  }

  const isPaid = order?.payment_status === 'paid' || paymentStatus === 'paid';
  const isCOD = order?.payment_method === 'cod';

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-16" data-testid="order-confirmation">
        <div className="text-center mb-12">
          {isPaid || isCOD ? (
            <>
              <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
              <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-4">
                Order Confirmed!
              </h1>
              <p className="text-muted-foreground">
                Thank you for your order. We've received your order and will begin processing it soon.
              </p>
            </>
          ) : sessionId && pollCount < 5 ? (
            <>
              <Loader2 className="h-20 w-20 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-4">
                Processing Payment...
              </h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment.
              </p>
            </>
          ) : (
            <>
              <XCircle className="h-20 w-20 text-red-500 mx-auto mb-6" />
              <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-4">
                Payment Pending
              </h1>
              <p className="text-muted-foreground">
                Your payment is still being processed. Please check back later.
              </p>
            </>
          )}
        </div>

        {order && (
          <div className="bg-surface rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-muted-foreground">Order Number</p>
                <p className="font-semibold text-lg">{order.order_number}</p>
              </div>
              <Package className="h-10 w-10 text-primary" />
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium capitalize">{order.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment</span>
                <span className="font-medium capitalize">
                  {isCOD ? 'Cash on Delivery' : isPaid ? 'Paid' : 'Pending'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Items</span>
                <span className="font-medium">{order.items?.length || 0}</span>
              </div>
              <div className="flex justify-between border-t pt-4">
                <span className="font-semibold">{t('total')}</span>
                <span className="font-semibold text-primary">
                  {order.total?.toFixed(2)} {currencySymbol}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild variant="outline" className="rounded-full px-8">
            <Link to="/shop">{t('continueShopping')}</Link>
          </Button>
          {order && (
            <Button asChild className="bg-primary hover:bg-purple-600 rounded-full px-8">
              <Link to={`/account/orders/${order.id}`}>
                View Order Details <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

// Success page for Stripe redirect
export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { API, clearCart } = useApp();
  const [status, setStatus] = useState('loading');
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPayment = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid') {
            setStatus('success');
            clearCart();
          } else if (pollCount >= 5) {
            setStatus('pending');
          } else {
            setPollCount((prev) => prev + 1);
          }
        }
      } catch (err) {
        if (pollCount >= 5) {
          setStatus('error');
        } else {
          setPollCount((prev) => prev + 1);
        }
      }
    };

    const timer = setTimeout(checkPayment, 2000);
    return () => clearTimeout(timer);
  }, [sessionId, API, clearCart, pollCount]);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="checkout-success">
        {status === 'loading' && (
          <>
            <Loader2 className="h-20 w-20 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-3xl font-bold mb-4">Verifying Payment...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-4">
              Payment Successful!
            </h1>
            <p className="text-muted-foreground mb-8">
              Thank you for your purchase. Your order is being processed.
            </p>
            <Button asChild className="bg-primary hover:bg-purple-600 rounded-full px-8">
              <Link to="/shop">Continue Shopping</Link>
            </Button>
          </>
        )}
        {status === 'pending' && (
          <>
            <Loader2 className="h-20 w-20 text-yellow-500 mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold mb-4">Payment Pending</h1>
            <p className="text-muted-foreground mb-8">
              Your payment is still being processed. You'll receive a confirmation email soon.
            </p>
            <Button asChild className="bg-primary hover:bg-purple-600 rounded-full px-8">
              <Link to="/shop">Continue Shopping</Link>
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="h-20 w-20 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold mb-4">Payment Error</h1>
            <p className="text-muted-foreground mb-8">
              There was an issue verifying your payment. Please contact support.
            </p>
            <Button asChild className="bg-primary hover:bg-purple-600 rounded-full px-8">
              <Link to="/contact">Contact Support</Link>
            </Button>
          </>
        )}
      </div>
    </Layout>
  );
};

// Cancel page for Stripe redirect
export const CheckoutCancelPage = () => {
  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="checkout-cancel">
        <XCircle className="h-20 w-20 text-red-500 mx-auto mb-6" />
        <h1 className="font-heading text-3xl font-bold text-[#1A1625] mb-4">
          Payment Cancelled
        </h1>
        <p className="text-muted-foreground mb-8">
          Your payment was cancelled. Your cart items are still saved.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild variant="outline" className="rounded-full px-8">
            <Link to="/shop">Continue Shopping</Link>
          </Button>
          <Button asChild className="bg-primary hover:bg-purple-600 rounded-full px-8">
            <Link to="/cart">Return to Cart</Link>
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default OrderConfirmationPage;
